import React from 'react';
import { Fragment, useState, useEffect } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Dialog, Transition, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, Square2StackIcon, CheckIcon } from '@heroicons/react/24/outline';
import TimeAgo from 'javascript-time-ago'
import en from 'javascript-time-ago/locale/en'

function RunnerWarning() {
  let [isOpen, setIsOpen] = useState(false);

  function closeModal() {
    setIsOpen(false);
  }

  function openModal() {
    setIsOpen(true);
  }

  return (
    <>
      <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon
              className="h-5 w-5 text-yellow-400"
              aria-hidden="true"
            />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              Some runners are unreachable.&nbsp;
              <span
                onClick={openModal}
                className="font-medium text-yellow-700 underline hover:text-yellow-600"
              >
                Troubleshooting help.
              </span>
            </p>
          </div>
        </div>
      </div>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                  <div className="mt-3 sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Troubleshooting help
                    </Dialog.Title>
                    <div className="mt-2">
                      <ol className="list-decimal px-4 text-sm text-gray-500">
                        <li className="mb-2">
                          Check the agent endpoint is accessible on the Internet
                        </li>
                        <li className="mb-2">Check the DNS entry for the endpoint matches.</li>
                        <li className="mb-2">Check 80/443 are open on the firewall or security group.</li>
                        <li className="mb-2">Run a <span className="font-semibold">curl -i</span> against the endpoint, it should give a 403 status code.</li>
                        <li className="mb-2">
                          Check the agent logs:
                          <div className="font-semibold">
                            sudo journalctl -u actuated --since today
                          </div>
                        </li>
                      </ol>
                      <a
                        href="https://docs.actuated.dev/troubleshooting/"
                        onClick={openModal}
                        className="my-6 text-sm text-blue-700 underline hover:text-blue-500"
                      >
                        View docs
                      </a>
                    </div>
                  </div>

                  <div className="mt-5 sm:mt-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                      onClick={closeModal}
                    >
                      Done.
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [copied]);

  return (
    <button
      type="button"
      onClick={() => {
        window.navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
        });
      }}
      className="absolute top-2 right-2 p-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <CheckIcon className="h-5 w-5" />
      ) : (
        <Square2StackIcon className="h-5 w-5" />
      )}
    </button>
  );
}

function LogsDialog({ open, onClose, selected, api }) {
  if (!selected) return null;

  const agentName = selected.agentName || selected.name;
  const owner = selected.owner || selected.customer;

  if (!agentName || !owner) {
    return null;
  }

  const command = `actuated-cli agent-logs --owner ${owner} \\
    ${agentName} \\
    --age 1h`;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="grow relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-col">
              <div className="px-4 sm:px-0">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900"
                >
                  Logs Command
                </DialogTitle>
                <p className="mt-2 text-sm text-gray-500">
                  Copy and run this command to view logs for this agent.
                </p>
              </div>
              <div className="mt-4 relative">
                <div className="relative bg-gray-900 rounded-lg p-4">
                  <CopyButton text={command} />
                  <pre className="text-sm text-gray-100 font-mono overflow-x-auto">
                    <code>{command}</code>
                  </pre>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => onClose(false)}
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Done
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

const ToolTip = ({ children, text }) => {
  return (
    <div className="group relative">
      {children}
      <div className="absolute group-hover:flex hidden ">
        <span className="fixed bg-gray-500 text-sm text-gray-100 rounded-md px-4 py-1 my-2">
          {text}
        </span>
      </div>
    </div>
  );
};

const RunnerStatusIcon = ({ status }) => {
  switch (status) {
    case 'running':
      return (
        <ToolTip text="Running">
          <div className="rounded-full w-2.5 h-2.5 bg-green-400"></div>
        </ToolTip>
      );
    case 'starting':
      return (
        <ToolTip text="Starting">
          <div className="rounded-full w-2.5 h-2.5 bg-gray-400"></div>
        </ToolTip>
      );
    case 'draining':
      return (
        <ToolTip text="Draining">
          <div className="rounded-full w-2.5 h-2.5 bg-gray-400"></div>
        </ToolTip>
      );
    default:
      return (
        <ToolTip text="Offline">
          <div className="rounded-full w-2.5 h-2.5 bg-red-400"></div>
        </ToolTip>
      );
  }
};

class RunnersTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      logsOpen: false,
      selectedLogsRunner: null,
    };
    this.setState({ runners: props.runners });

    TimeAgo.setDefaultLocale(en.locale)
    TimeAgo.addLocale(en)
    this.state.timeAgo = new TimeAgo('en-US')

  }

  openLogs = (runner) => {
    // Get owner from API
    const owners = this.props.api?.getOwners() || [];
    const owner = owners[0] || runner.customer;
    
    this.setState({
      selectedLogsRunner: {
        name: runner.name,
        agentName: runner.name,
        owner: owner,
        customer: runner.customer,
      },
      logsOpen: true,
    });
  }

  render() {
    let runners = this.props.runners;
    console.log(
      'Rendering: props',
      this.props.runners,
      'state',
      this.state.runners
    );

    let formatArch = (arch) => {
      if (!arch) {
        return (
          <ToolTip text="Unknown">
            <span>-</span>
          </ToolTip>
          )
      } else if (arch == "arm64") {
        return (
        <ToolTip text="arm64">
          <span>💪</span>
        </ToolTip>
        )
      } else {
        return (
          <ToolTip text="amd64">
            <span>🖥️</span>
          </ToolTip>
          )
      }
    }

    // Sort runners: online status first, then customer name, then machine name
    if (runners) {
      runners = [...runners].sort((a, b) => {
        // First sort by online status (reachable first)
        const aReachable = a.reachable ? 1 : 0;
        const bReachable = b.reachable ? 1 : 0;
        if (aReachable !== bReachable) {
          return bReachable - aReachable; // reachable (1) comes before unreachable (0)
        }
        
        // Then sort by customer name (alphabetic)
        const aCustomer = (a.customer || '').toLowerCase();
        const bCustomer = (b.customer || '').toLowerCase();
        if (aCustomer !== bCustomer) {
          return aCustomer.localeCompare(bCustomer);
        }
        
        // Finally sort by machine name (alphabetic)
        const aName = (a.name || '').toLowerCase();
        const bName = (b.name || '').toLowerCase();
        return aName.localeCompare(bName);
      });
    }

    return (
      <>
        {this.state.selectedLogsRunner && (
          <LogsDialog
            api={this.props.api}
            open={this.state.logsOpen}
            onClose={() => {
              this.setState({ logsOpen: false });
            }}
            selected={this.state.selectedLogsRunner}
          />
        )}
        <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-semibold text-gray-900">
              Available runners
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Runners registered for your organisation
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              onClick={this.props.onRefresh}
              type="button"
              disabled={this.props.refreshing}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
            >
              {this.props.refreshing ? (
                <svg
                  className="animate-spin h-5 w-5 mr-3"
                  viewBox="0 0 24 24"
                  visibility={this.props.refreshing ? 'show' : 'hidden'}
                >
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                ''
              )}
              {this.props.refreshing ? 'Refreshing' : 'Refresh'}
            </button>
          </div>
        </div>
        <div className="mt-8 flex flex-col">
          <div className="mb-4">
            {runners && runners.some((runner) => runner.reachable == false) && (
              <RunnerWarning onTroubleshoot={this.props.onTroubleshoot} />
            )}
          </div>
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="whitespace-nowrap py-3.5 pl-4 pr-3 sm:pl-6"
                      ></th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell lg:table-cell"
                      >
                        Arch.
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Ping
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        RAM
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Free RAM
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        CPUs
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        VMs
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell lg:table-cell"
                      >
                        Agent up
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {runners ? (
                      runners.map((runner) => (
                        <tr key={runner.name}>
                          <td className="whitespace-nowrap w-4 px-2 py-2 sm:pl-6 pl-2 text-sm font-medium text-gray-900">
                            <RunnerStatusIcon status={runner.status} />
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 pr-3 text-sm text-gray-900">
                            <div className="flex flex-col">
                              <span className="font-medium">{runner.name}</span>
                              {runner.customer && (
                                <span className="text-gray-600 text-xs font-normal">{runner.customer}</span>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-500 hidden md:table-cell lg:table-cell">
                            {formatArch(runner.arch)}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">
                            {runner.reachable
                              ? (runner.pingNano / 1000 / 1000).toFixed(0) +
                                'ms'
                              : '-'}{' '}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-500">
                            {runner.reachable
                              ? Math.floor(
                                  runner.memory / 1000 / 1000 / 1000
                                ).toFixed(0) + 'GB'
                              : '-'}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-500">
                            {runner.reachable
                              ? Math.floor(
                                  runner.memoryAvailable / 1000 / 1000 / 1000
                                ).toFixed(0) + 'GB'
                              : '-'}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-500">
                            {runner.reachable ? runner.cpus : '-'}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-500">
                            {runner.reachable
                              ? `${runner.vms}/${runner.maxVms}`
                              : '-'}
                          </td>

                          <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-500 hidden md:table-cell lg:table-cell">
                            {runner.reachable ? (
                              this.state.timeAgo.format(new Date(new Date().getTime() - (runner.uptimeNano / 1000 / 1000)), 'mini')
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">
                            <button
                              type="button"
                              className="inline-flex z-0 items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white disabled:text-gray-400"
                              onClick={() => this.openLogs(runner)}
                              disabled={!runner.reachable}
                            >
                              Logs
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr key="empty-row">
                        <td
                          className="whitespace-nowrap px-2 py-4 py-2 pr-3 sm:pl-6 pl-2 text-sm text-gray-900"
                          colSpan={9}
                        >
                          No runners available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }
}

export default RunnersTable;
